import React, { useState } from "react";
import {
  IconButton,
  Tooltip,
  CircularProgress,
  Typography,
  Box,
  alpha,
  useTheme,
} from "@mui/material";
import { TableChart } from "@mui/icons-material";
import * as XLSX from "xlsx";
import api from "../../api";

// Profilde kullanılan tüm hesaplama modüllerini ve sabitleri dahil ediyoruz
import {
  calculateAkkClass,
  getAkkDepthCode,
  getAkkTextureAndPerm,
  akkFormulu,
  indeksFormulu,
  akkFormulParcalari,
} from "../../utils/akkUtils";
import { numarayaGoreSirala, dosyaAdi } from "../../utils/exportUtils";
import {
  FIZYOGRAFYA_OPTIONS,
  SU_EROZYON_DERECESI_OPTIONS,
  ARAZI_KULLANIM_KATEGORI_OPTIONS,
} from "../../constants/fieldConstants";
import { TEKSTUR_OPTIONS } from "../../constants/horizonConstants";
import { calculateStorieIndex } from "../../utils/storieUtils";
import { fetchAllSondasByJob } from "../../utils/sondaFetch";
import { calculateLegend } from "../../utils/legendUtils";

export default function SondaExcelExportButton({ jobId, jobName }) {
  const theme = useTheme();
  const [loading, setLoading] = useState(false);

  const handleExport = async () => {
    setLoading(true);
    try {
      // 1. HEM SONDALARI HEM DE PROFİLLERİ AYNI ANDA ÇEKİYORUZ (Eksik verileri tamamlamak için)
      // Sonda ucu sayfali oldugu icin butun sayfalar dolasilarak cekiliyor.
      const [jobSondas, profileRes] = await Promise.all([
        fetchAllSondasByJob(jobId),
        api.get(`/admin/job-profiles/${jobId}`), // İşe ait profiller
      ]);

      if (jobSondas.length === 0) {
        alert("Bu çalışmaya ait kayıtlı sonda bulunamadı.");
        setLoading(false);
        return;
      }

      // Profilleri Parse Et
      const jobProfiles = profileRes.data.success ? profileRes.data.data : [];
      const parsedProfiles = jobProfiles.map((p) => {
        let fullData = {};
        try {
          fullData =
            typeof p.full_data === "string"
              ? JSON.parse(p.full_data)
              : p.full_data;
        } catch (e) {}
        return { ...p, ...fullData };
      });

      const excelRows = [];

      // 2. HER SONDA İÇİN VERİLERİ HARMANLA VE HESAPLA
      // Sondalar KENDI numaralarina gore kucukten buyuge (S1, S2 ... S100).
      numarayaGoreSirala(jobSondas, "sonda_no").forEach((sonda) => {
        let s = {};
        try {
          s =
            typeof sonda.full_data === "string"
              ? JSON.parse(sonda.full_data)
              : sonda.full_data;
        } catch (e) {
          return; // Hatalı veriyi atla
        }

        // BENZER PROFİLİ BUL VE EKSİK VERİLERİ TAMAMLA
        // Sondada benzer_profil duz sayi ("596"), profilde profil_no "P596"
        // olarak tutuluyor; ikisi de rakama indirgenerek eslestirilir. (Duz
        // karsilastirmada cogu sonda profilini bulamiyor, profil grubu bos
        // kaldigi icin indeks formulu izah cetvelinden farkli cikiyordu.)
        const profilAnahtar = (v) => String(v ?? "").replace(/\D/g, "");
        const targetProfileNo = profilAnahtar(s.benzer_profil);
        const baseProfile =
          (targetProfileNo &&
            parsedProfiles.find(
              (p) => profilAnahtar(p.profil_no) === targetProfileNo,
            )) ||
          {};

        const getValue = (key) => {
          if (s[key] !== undefined && s[key] !== null && s[key] !== "")
            return s[key];
          return baseProfile[key] || "";
        };

        const activeHorizons =
          s.horizons && s.horizons.length > 0
            ? s.horizons
            : baseProfile.horizons || [];

        // Hesaplama motorlarına girecek SANAL PROFIL verisini oluştur (Sonda + Profil Karışımı)
        const d = {
          ...baseProfile,
          ...s,
          tesirli_derinlik:
            getValue("derinlik") || getValue("tesirli_derinlik"),
          egim_yuzde: getValue("egim") || getValue("egim_yuzde"),
          tabii_drenaj: getValue("drenaj") || getValue("tabii_drenaj"),
          su_erozyon_derecesi:
            getValue("su_erozyonu") || getValue("su_erozyon_derecesi"),
          // Profil grubu benzer profilden gelir. (Eskiden bulunamazsa sondanin
          // grup_no'su yaziliyordu; o profil grubu degildir ve indeks formulunu
          // izah cetvelinden farkli yapiyordu.)
          profilgrup: getValue("profilgrup"),
          tuzluluk: getValue("tuzluluk"),
          alkalilik: getValue("alkalilik"),
          bor: getValue("bor"),
          taslilik: getValue("taslilik"),
          kayalilik: getValue("kayalilik"),
          cakillilik: getValue("cakillilik"),
          arazi_kullanim_sinif: getValue("arazi_kullanim_sinif"),
          btg: getValue("btg"),
          ana_materyal: getValue("ana_materyal"),
          fizyografya: getValue("fizyografya"),
          horizons: activeHorizons,
        };

        // HESAPLAMALARI ÇALIŞTIR
        const akk = calculateAkkClass(d);
        const storie = calculateStorieIndex(d);
        const legend = calculateLegend(d);

        const firstHor = activeHorizons[0] || {};

        const takenSamples = activeHorizons.filter((h) => !h.ornek_alinamadi);
        const sampleDepthsString =
          takenSamples.length > 0
            ? `${takenSamples.length}N:${takenSamples.map((h) => `(${h.derinlik_min}-${h.derinlik_max || "?"})`).join("")}`
            : "-";

        const horizonSequence = activeHorizons
          .map((h) => h.sembol)
          .filter((str) => str)
          .join("-");

        let targetHor = null;
        if (activeHorizons.length === 1) targetHor = activeHorizons[0];
        else if (activeHorizons.length === 2) targetHor = activeHorizons[1];
        else if (activeHorizons.length > 2)
          targetHor = activeHorizons[activeHorizons.length - 2];

        const permInfo = getAkkTextureAndPerm(
          targetHor?.tekstur,
          targetHor?.kirec,
        );
        const permVal = permInfo.val || "";

        // Ust / alt toprak tekstur sutunlari formulle AYNI kaynaktan gelir.
        // Cok sig toprakta (derinlik 5) alt toprak yoktur, "att" bos kalir.
        const parcalar = akkFormulParcalari(d, akk.formulaPermeability);
        const topTexCodeStr = parcalar.ustHarf;
        const botTexCodeStr = parcalar.altHarf;

        const rawSlopeCode = legend.slopeCode.trim();
        const slopeLetterOnly = rawSlopeCode.charAt(0) || "";
        const slopeNumberOnly = rawSlopeCode.substring(1);

        const eroNumber = d.su_erozyon_derecesi?.charAt(0) || "1";
        const depthAkk = getAkkDepthCode(d.tesirli_derinlik);

        // --- PROFİL T-ŞABLONUNDAKİ FAKTÖR DEĞERLERİ EŞLEŞTİRMELERİ ---
        const eroFaktorMap = { 1: "100", 2: "90", 3: "80", 4: "50" };
        const eroFaktoru = eroFaktorMap[eroNumber] || "100";

        let akkDrain = "";
        let drenajDerece = "";
        if (d.tabii_drenaj) {
          const drText = d.tabii_drenaj;
          if (drText.includes("yok") || drText.includes("(İ)")) {
            akkDrain = "";
            drenajDerece = "100";
          } else if (drText.includes("Sulamada") || drText.includes("(K)")) {
            akkDrain = "K";
            drenajDerece = "85";
          } else if (drText.includes("F1")) {
            akkDrain = "F1";
            drenajDerece = "65";
          } else if (drText.includes("F2")) {
            akkDrain = "F2";
            drenajDerece = "50";
          } else if (
            drText.includes("F3") ||
            drText.toLowerCase().includes("göllenme")
          ) {
            akkDrain = "F3";
            drenajDerece = "25";
          }
        }

        const tuzCode = d.tuzluluk
          ? d.tuzluluk.match(/\d+/)
            ? d.tuzluluk.match(/\d+/)[0]
            : "1"
          : "";
        const tuzFaktorMap = { 1: "100", 2: "80", 3: "50", 4: "50" };
        const tuzFaktoru = tuzFaktorMap[tuzCode] || "";

        const alkCode = d.alkalilik
          ? d.alkalilik.match(/X\d+/)
            ? d.alkalilik.match(/X\d+/)[0]
            : d.alkalilik.includes("Çok Fazla") || d.alkalilik.includes("X4")
              ? "X4"
              : d.alkalilik.includes("Orta") || d.alkalilik.includes("X2")
                ? "X2"
                : d.alkalilik.includes("Fazla") || d.alkalilik.includes("X3")
                  ? "X3"
                  : "X1"
          : "";
        const alkFaktorMap = { X1: "100", X2: "80", X3: "65", X4: "30" };
        const alkFaktoru = alkFaktorMap[alkCode] || "";

        const borCode = d.bor
          ? d.bor.includes("B")
            ? d.bor.match(/B\d+/)?.[0] || "B1"
            : "B1"
          : "";
        const borFaktorMap = { B1: "100", B2: "80", B3: "60", B4: "50" };
        const borFaktoru = borFaktorMap[borCode] || "";

        const kayaCode = d.kayalilik
          ? d.kayalilik.match(/r\d/i)
            ? d.kayalilik.match(/r\d/i)[0].toLowerCase()
            : d.kayalilik.includes("Az")
              ? "r0"
              : ""
          : "";
        const kayaFaktorMap = {
          r0: "95",
          r1: "70",
          r2: "50",
          r3: "50",
          r4: "50",
        };
        const kayaFaktoru = kayaFaktorMap[kayaCode] || "100";

        // --- PROFİL T-ŞABLONUNDAKİ METİN ÇEVİRİ YARDIMCILARI ---
        const getLabelByCode = (code) => {
          const option = FIZYOGRAFYA_OPTIONS.find((item) => item.code === code);
          return option ? option.label : "Kod bulunamadı";
        };

        const getTeksturAciklama = (kod) => {
          if (!kod) return "";
          const tamMetin = TEKSTUR_OPTIONS.find((option) =>
            option.startsWith(kod + " "),
          );
          if (tamMetin) return tamMetin.split("(")[1].split(")")[0];
          return kod;
        };

        const getErozyonDereceMetni = (dereceNo) => {
          if (!dereceNo) return "";
          const bulundu = SU_EROZYON_DERECESI_OPTIONS.find((opt) =>
            opt.startsWith(dereceNo.toString().trim()),
          );
          if (bulundu) return bulundu.split("-")[1].split("(")[0].trim();
          return dereceNo;
        };

        const getTuzlulukAciklama = (val) => {
          const s = String(val).trim();
          if (s === "1") return "TUZSUZ";
          if (s === "2") return "HAFİF TUZLU";
          if (s === "3") return "ORTA TUZLU";
          if (s === "4") return "ŞİDDETLİ TUZLU";
          return s;
        };

        const getAlkalilikAciklama = (val) => {
          const s = String(val).toUpperCase().trim();
          if (s === "X1" || s === "1") return "HAFİF";
          if (s === "X2" || s === "2") return "ORTA";
          if (s === "X3" || s === "3") return "FAZLA";
          if (s === "X4" || s === "4") return "ÇOK FAZLA";
          return s;
        };

        const getBorAciklama = (val) => {
          const s = String(val).toUpperCase().trim();
          if (s === "B1" || s === "1") return "Borsuz";
          if (s === "B2" || s === "2") return "Hafif Borlu";
          if (s === "B3" || s === "3") return "Orta Borlu";
          if (s === "B4" || s === "4") return "Yüksek Borlu";
          return s;
        };

        const getAraziKullanimMetni = (fullText) => {
          if (!fullText) return "";
          return fullText.includes("(")
            ? fullText.split("(")[0].trim()
            : fullText.trim();
        };

        const getDrenajDurumuMetni = (fullText) => {
          if (!fullText) return "";
          const codeMatch = fullText.match(/\(([^)]+)\)/);
          const code = codeMatch ? codeMatch[1] : "";
          if (code === "İ") return "İyi";
          if (code === "K") return "Yararlı";
          if (code.startsWith("F")) return "Fena";
          return fullText;
        };

        const getSymbolOnly = (val) => {
          if (!val) return "";
          if (val.includes("-")) return val.split("-")[0].trim();
          const pMatch = val.match(/\(([^)]+)\)$/);
          if (pMatch) {
            if (pMatch[1].length > 3) return val.split(" ")[0].trim();
            return pMatch[1].trim();
          }
          return val.split(" ")[0].trim();
        };

        const cleanPhase = (val) => {
          if (!val) return "";
          const lower = val.toLowerCase();
          if (
            lower.includes("yok") ||
            lower.includes("taşsız") ||
            lower.includes("r0")
          )
            return "";
          return val.split("-")[0].trim();
        };

        const getSafeString = (val) => (val ? val : "");

        const formatNumber = (num) => {
          if (num === undefined || num === null || num === "") return "";
          const parsed = parseFloat(num);
          return isNaN(parsed) ? "" : parsed.toFixed(2);
        };

        // Formüllerin Hesaplanması
        // AKK ve indeks formulu BUTUN ciktilarda ayni: ortak ureticiden gelir.
        const akkFormulStr = akkFormulu(d);
        const cleanNdxFormulStr = indeksFormulu(d);

        // 3. İSTENEN BÜTÜN SÜTUNLARI DİZ (PROFİL İLE BİREBİR AYNI - SADECE BAŞI FARKLI)
        excelRows.push({
          Sonda_no: s.sonda_no || "",
          profilgrup: d.profilgrup || "",
          "Örnek Sonda id": s.benzer_sonda_no || "",
          derinlik: depthAkk,
          utt: topTexCodeStr,
          att: botTexCodeStr,
          // Alt toprak yoksa (cok sig, derinlik 5) gecirgenligi de yazilmaz.
          alttprgec: botTexCodeStr ? permVal : "",
          tuz: tuzCode > 1 ? tuzCode : "",
          alkalilik: alkCode !== "X1" ? alkCode : "",
          bor: borCode !== "B1" ? borCode : "",
          drenaj: akkDrain,
          taslilik: cleanPhase(d.taslilik),
          Çakıl: cleanPhase(d.cakillilik),
          kayalilik: cleanPhase(d.kayalilik),
          kuldurum: getSymbolOnly(d.arazi_kullanim_sinif),
          egim: slopeNumberOnly,
          egimsinif: slopeLetterOnly,
          erozyon: eroNumber,
          ndx_formul: cleanNdxFormulStr,
          a_degeri: formatNumber(storie.factorA),
          b_degeri: formatNumber(storie.factorB),
          c_degeri: formatNumber(storie.factorC),
          drenaj_d: drenajDerece,
          tuz_d: tuzFaktoru,
          alkalid: alkFaktoru,
          bor_d: borFaktoru,
          erozyon_d: eroFaktoru,
          kayalik_d: kayaFaktoru,
          x_diger: formatNumber(storie.factorX),
          deger_topl: formatNumber(
            storie.factorA * storie.factorB * storie.factorC * storie.factorX,
          ),
          index: formatNumber(storie.finalIndex),
          index_ack: `A=${formatNumber(storie.factorA)}, B=${formatNumber(storie.factorB)}, C=${formatNumber(storie.factorC)}, X=${formatNumber(storie.factorX)}, (I=${formatNumber(storie.finalIndex)})`,
          akk_formul: akkFormulStr,
          akk_sinifi: akk.finalClass,
          alttahdit: akk.subClassString,
          x: formatNumber(d.koordinat_x),
          y: formatNumber(d.koordinat_y),
          z: formatNumber(d.yukseklik),
          kururenk:
            `${firstHor.renk_sinif_kuru || ""} ${firstHor.renk_kod_kuru || ""}`.trim(),
          yasrenk:
            `${firstHor.renk_sinif_yas || ""} ${firstHor.renk_kod_yas || ""}`.trim(),
          btg: getSafeString(d.btg),
          amateryal: getSafeString(d.ana_materyal),
          fzyografya: getSafeString(d.fizyografya),
          utt_ack: getTeksturAciklama(firstHor.tekstur),
          // "att" ile ayni katman: ikinci katman (yoksa ilki); cok sigda bos.
          att_ack: botTexCodeStr
            ? getTeksturAciklama((activeHorizons[1] || firstHor).tekstur)
            : "",
          pozsyn_ack: getSafeString(getLabelByCode(d.fizyografya)),
          erzyn_ack: getErozyonDereceMetni(d.su_erozyon_derecesi),
          derinlikcm: formatNumber(Math.round(Number(d.tesirli_derinlik))),
          tslk_ack: getSafeString(d.taslilik),
          tuz_ack: getTuzlulukAciklama(getSafeString(d.tuzluluk)),
          alk_ack: getAlkalilikAciklama(getSafeString(d.alkalilik)),
          yuzde_egim: getSafeString(d.egim_yuzde),
          kylk_ack: getSafeString(d.kayalilik),
          bor_ack: getBorAciklama(getSafeString(d.bor)),
          arz_klnm: getAraziKullanimMetni(
            getSafeString(d.arazi_kullanim_sinif),
          ),
          drnj_ack: getDrenajDurumuMetni(getSafeString(d.tabii_drenaj)),
          urun: getSafeString(d.urun),
          alnornsay: sampleDepthsString,
          hrzn_dizil: horizonSequence || "",
          SERI_KOD: getSafeString(d.seri_kodu),
          SERI_ADI: getSafeString(d.toprak_serisi),
          SERI_LEJAN:
            `${legend.leftCode} [(${legend.topString}) / (${legend.bottomString || "-"})] ${legend.rightCode}`
              .replace(/\s+/g, " ")
              .trim(),
          ARAZI_TIPI: getSafeString(d.arazi_kullanim_detay),
          ARAZI_FORM: "",
          AraziSekli: getSafeString(d.mikrorolyef),
          ORT_EGIM: "",
        });
      });

      // 4. EXCEL OLUŞTUR VE İNDİR
      const wb = XLSX.utils.book_new();
      const ws = XLSX.utils.json_to_sheet(excelRows);

      const narrowCols = [
        "profilgrup",
        "derinlik",
        "utt",
        "att",
        "alttprgec",
        "tuz",
        "alkalilik",
        "bor",
        "drenaj",
        "taslilik",
        "Çakıl",
        "kuldurum",
        "kayalilik",
        "egim",
        "egimsinif",
        "erozyon",
        "a_degeri",
        "b_degeri",
        "c_degeri",
        "x_diger",
        "x",
        "y",
        "z",
      ];

      const wscols = Object.keys(excelRows[0]).map((key) => {
        if (narrowCols.includes(key)) return { wch: 6 };
        if (key === "alnornsay" || key === "ndx_formul") return { wch: 30 };
        if (
          key.includes("_ack") ||
          key.includes("formul") ||
          key.includes("ADI") ||
          key.includes("LEJAN")
        )
          return { wch: 25 };
        return { wch: 12 };
      });

      ws["!cols"] = wscols;

      XLSX.utils.book_append_sheet(wb, ws, "Tüm_Sondalar");
      XLSX.writeFile(wb, dosyaAdi(jobName, "T Şablon Sondalar", "xlsx"));
    } catch (error) {
      console.error(error);
      alert("Toplu Sonda Excel'i oluşturulurken bir hata oluştu.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Tooltip title="Bu çalışmadaki TÜM SONDALARI detaylı T-Şablon Excel tablosunda indirir">
      <Box
        sx={{ display: "flex", flexDirection: "column", alignItems: "center" }}
      >
        <IconButton
          color="secondary"
          onClick={handleExport}
          disabled={loading}
          sx={{
            bgcolor: alpha("#9c27b0", 0.05),
            mb: 0.5,
            "&:hover": { bgcolor: alpha("#9c27b0", 0.15) },
          }}
        >
          {loading ? (
            <CircularProgress size={20} color="secondary" />
          ) : (
            <TableChart fontSize="small" />
          )}
        </IconButton>
        <Typography
          variant="caption"
          sx={{
            fontSize: "0.6rem",
            color: "text.secondary",
            fontWeight: "bold",
            whiteSpace: "nowrap",
          }}
        >
          S-ŞABLON
        </Typography>
      </Box>
    </Tooltip>
  );
}
